/**
 * Overview — plain-language tools grid from /auth/me.capability_catalog.
 * User copy only: no env flags, no internal maturity jargon, no fabric/adapter IDs.
 *
 * Catalog href: enabled capabilities keep the catalog `#/` href.
 * Disabled capabilities stay href="" ("Not available yet") — never refill
 * from SOFT_HREF (legacy map; old routes like #/response-plans). Preview
 * surfaces must use a separate preview_href, never the Live href.
 */

import { escapeHtml } from "./dom.js";

/** Prefer operator language over catalog engineering labels. */
export const PLAIN_LABELS = {
  readiness: "Before tonight",
  response_plans: "Response plans",
  response_drill: "Practice drill",
  device_health: "Device health",
  health_engine: "Health checks",
  predictive: "Maintenance advisories",
  sop_knowledge: "Playbooks",
  incidents: "Incidents",
  responder_dispatch: "Response team",
  ai_incident: "AI Context",
  swarm: "Coordinated response",
  siren: "Siren control",
  camera: "Camera snapshots",
  phone_locations: "Team on map",
  ops_map: "Home map",
  automation: "Auto rules",
  actuators: "Connected equipment",
  external_robots: "Field robots",
  external_robot_commands: "Robot commands",
  proof: "Response proof",
  pir: "After-action review",
  audit: "Audit log",
  exports: "Exports",
  devices: "All boards",
  activate: "Activate board",
  device_capabilities: "Board features",
  groups: "Groups",
  site: "Site view",
  ops_live: "Platform health",
  ota: "Firmware updates",
  billing: "Billing",
  voice: "Voice alerts",
  tenant_acl: "Team access",
  admin_hub: "Administration",
  notifications: "Notifications",
  manual_backup: "Backup",
  backup_worker: "Scheduled backup",
  admin_security: "Security controls",
  mac_acl_audit: "Network access audit",
  physical_inhibit: "Site emergency stop",
  adapter_certification: "Equipment approvals",
  adapter_cert: "Equipment approvals",
  site_authority: "Site emergency stop",
  delivery_ledger: "Command delivery proof",
  cross_tenant_share: "Organization shares",
  sa_team: "Staff accounts",
  croc_fde: "CROC-FDE invite",
  federation: "Multi-site HQ",
  platform_control: "Platform",
};

/** Legacy id→hash map. Do not use to refill disabled catalog cards. */
export const SOFT_HREF = {
  readiness: "#/readiness",
  response_plans: "#/response-plans",
  response_drill: "#/response-drill",
  device_health: "#/overview",
  health_engine: "#/readiness?tab=health",
  predictive: "#/readiness",
  sop_knowledge: "#/response-plans?tab=sop",
  incidents: "#/incidents",
  responder_dispatch: "#/response",
  ai_incident: "#/overview",
  swarm: "#/orchestration",
  siren: "#/response?tab=siren",
  camera: "#/snapshots",
  phone_locations: "#/overview",
  ops_map: "#/overview",
  automation: "#/automation",
  actuators: "#/orchestration?tab=actuators",
  external_robots: "#/orchestration?tab=robots",
  external_robot_commands: "#/orchestration?tab=robots",
  proof: "#/incidents",
  pir: "#/incidents",
  audit: "#/audit-log",
  exports: "#/audit-log?source=exports",
  devices: "#/group/_all",
  activate: "#/activate",
  device_capabilities: "#/devices?view=capabilities",
  groups: "#/groups",
  site: "#/site",
  ops_live: "#/ops-live",
  ota: "#/ota",
  billing: "#/billing",
  voice: "#/voice",
  tenant_acl: "#/tenant-delegations",
  admin_hub: "#/admin",
  notifications: "#/settings",
  manual_backup: "#/settings",
  backup_worker: "#/platform-control?tab=backup",
  admin_security: "#/platform-control?tab=security",
  mac_acl_audit: "#/platform-control?tab=security",
  physical_inhibit: "#/platform-control?tab=security",
  adapter_certification: "#/platform-control?tab=security",
  adapter_cert: "#/platform-control?tab=security",
  site_authority: "#/platform-control?tab=security",
  delivery_ledger: "#/platform-control?tab=security",
  cross_tenant_share: "#/platform-control?tab=shares",
  sa_team: "#/sa-team",
  croc_fde: "#/croc-fde",
  federation: "#/platform-control?tab=federation",
  platform_control: "#/platform-control",
};

/** Short operator blurbs — never echo backend catalog engineering text. */
export const PLAIN_BLURBS = {
  readiness: "Scores and gaps before an event.",
  response_plans: "Versioned response plans you can certify.",
  response_drill: "Practice drills without live alarms.",
  device_health: "Board presence and health signals.",
  health_engine: "Deeper health scoring when enabled.",
  predictive: "Maintenance suggestions for boards.",
  sop_knowledge: "Playbooks and operator feedback.",
  incidents: "Live timeline, SLA, and closure.",
  responder_dispatch: "My queue and team response.",
  ai_incident: "Home AI Context — same Decision Surface as the map.",
  swarm: "Coordinated multi-resource response.",
  siren: "Board and multi-board siren control.",
  camera: "Event-linked camera snapshots.",
  phone_locations: "Team locations on the map.",
  ops_map: "One Home map — boards, alarms, and people.",
  automation: "Rules that act during incidents.",
  actuators: "Registered response equipment.",
  external_robots: "Registered field robots for coordinated response.",
  external_robot_commands: "Approved robot actions when two-person approval is ready.",
  proof: "Evidence bundle after a response.",
  pir: "Findings and corrective actions.",
  audit: "Activity, channels, and audit trail.",
  exports: "Spreadsheet and PDF downloads.",
  devices: "Claim, configure, and command boards.",
  activate: "Bring a new board into the account.",
  device_capabilities: "Features registered on each board.",
  groups: "Group cards, sharing, and alarm timing.",
  site: "Site-scoped lookup for operators.",
  ops_live: "Platform health metrics for staff.",
  ota: "Firmware inventory and rollouts.",
  billing: "Plan, usage, and invoices.",
  voice: "Phone alert templates and test calls.",
  tenant_acl: "Delegated team access.",
  admin_hub: "Users and account administration.",
  notifications: "Email, Telegram, and push recipients.",
  manual_backup: "Create and inspect backups.",
  backup_worker: "Scheduled backups and retention.",
  admin_security: "Security controls and rotation.",
  mac_acl_audit: "Network allowlist review.",
  physical_inhibit: "Emergency stop for a site or resource.",
  adapter_certification: "Approve connected equipment before use.",
  adapter_cert: "Approve connected equipment before use.",
  site_authority: "Emergency stop for a site.",
  delivery_ledger: "Signed delivery and acknowledgment proof.",
  cross_tenant_share: "Share access across organizations.",
  sa_team: "Delegated staff accounts.",
  croc_fde: "Create single-use CROC-FDE register links.",
  federation: "Multi-site headquarters view.",
  platform_control: "Platform domains and ops console.",
};

const CATEGORY_ORDER = ["Before", "During", "After", "Platform"];

export const CATEGORY_PLAIN = {
  Before: "Before an event",
  During: "During an event",
  After: "After an event",
  Platform: "Account & platform",
};

function softTitle(id, rawLabel) {
  let title = PLAIN_LABELS[id] || String(rawLabel || id || "Tool");
  return title
    .replace(/\s*\(Gate-\d+[^)]*\)/gi, "")
    .replace(/\bPhysical inhibit\b/gi, "Site emergency stop")
    .replace(/\bAdapter certification\b/gi, "Equipment approvals")
    .replace(/\bDelivery\s*\/\s*signed-ACK ledger\b/gi, "Command delivery proof")
    .replace(/\bExternal robots?\b/gi, "Field robots")
    .replace(/\bExternal robot commands?\b/gi, "Robot commands")
    .replace(/\((?:DJI|Unitree)[^)]*\)/gi, "")
    .replace(/\bbounded\b/gi, "")
    .replace(/\bGate-\d+\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function resolveSoftHref(id, rawHref, enabled) {
  if (!enabled) return "";
  const fromCatalog = String(rawHref || "").trim();
  if (fromCatalog.startsWith("#/")) return fromCatalog;
  return "";
}

/**
 * @param {object} item
 * @returns {{ title: string, status: string, statusCls: string, href: string, ready: boolean, blurb: string }}
 */
export function plainCapabilityEntry(item) {
  const id = String((item && item.id) || "");
  const title = softTitle(id, item && item.label);
  const enabled = !!(item && item.enabled);
  const href = resolveSoftHref(id, item && item.href, enabled);
  const blurb = PLAIN_BLURBS[id] || "";
  if (enabled && href) {
    return { title, status: "Ready", statusCls: "online", href, ready: true, blurb };
  }
  if (enabled) {
    return { title, status: "Listed", statusCls: "neutral", href: "", ready: false, blurb };
  }
  return { title, status: "Not available yet", statusCls: "offline", href: "", ready: false, blurb };
}

/**
 * Honesty rows for concepts without SPA catalog pages (no internal product codes).
 * Linked when soft surfaces exist.
 * @returns {Array<{ title: string, status: string, statusCls: string, href: string, ready: boolean, blurb: string }>}
 */
export function verifiedResponseHonestyEntries() {
  return [
    {
      title: "Verified response network",
      status: "Preview",
      statusCls: "neutral",
      href: "#/platform-control?tab=security",
      ready: true,
      blurb: "Emergency stop, equipment approvals, and delivery proof live under Platform · Security.",
    },
    {
      title: "Shared resource network",
      status: "Preview",
      statusCls: "neutral",
      href: "#/orchestration",
      ready: true,
      blurb: "Response team, equipment, and field robots coordinate under Response team.",
    },
    {
      title: "CROC-FDE register (Sentinel)",
      status: "Ready",
      statusCls: "online",
      href: "#/croc-fde",
      ready: true,
      blurb: "Create a single-use register link here. Recipients redeem once — no public signup.",
    },
    {
      title: "Spatial Compute Fabric",
      status: "Preview",
      statusCls: "neutral",
      href: "#/fabric",
      ready: true,
      blurb: "See these workers. Candidates only — Accept is not Publish.",
    },
  ];
}

/**
 * @param {object|null|undefined} me
 * @returns {string}
 */
export function buildOverviewCapabilityEntriesHtml(me) {
  const catalog = me && Array.isArray(me.capability_catalog) ? me.capability_catalog : [];
  const sections = CATEGORY_ORDER.map((category) => {
    const rows = catalog
      .filter((item) => item && item.category === category)
      .map(plainCapabilityEntry);
    if (!rows.length) return "";
    const readyN = rows.filter((r) => r.status === "Ready").length;
    const cards = rows
      .map((r) => {
        const action = r.href
          ? `<a class="btn sm secondary btn-tap" href="${escapeHtml(r.href)}">Open</a>`
          : `<span class="muted" style="font-size:11px">—</span>`;
        const blurb = r.blurb
          ? `<p class="muted" style="margin:6px 0 0;font-size:12px;line-height:1.4">${escapeHtml(r.blurb)}</p>`
          : "";
        return `<article class="croc-cc-tool-card glass-panel glass-panel--inset">
          <div class="croc-cc-tool-card__top">
            <strong>${escapeHtml(r.title)}</strong>
            <span class="badge ${escapeHtml(r.statusCls)}">${escapeHtml(r.status)}</span>
          </div>
          ${blurb}
          <div class="croc-cc-tool-card__act">${action}</div>
        </article>`;
      })
      .join("");
    return `<section class="croc-cc-tools__sec">
      <div class="croc-cc-tools__sec-head">
        <h5>${escapeHtml(CATEGORY_PLAIN[category] || category)}</h5>
        <span class="muted">${readyN}/${rows.length} ready</span>
      </div>
      <div class="croc-cc-tools__grid">${cards}</div>
    </section>`;
  }).join("");

  const honesty = verifiedResponseHonestyEntries()
    .map(
      (r) => `<article class="croc-cc-tool-card glass-panel glass-panel--inset croc-cc-tool-card--honest">
      <div class="croc-cc-tool-card__top">
        <strong>${escapeHtml(r.title)}</strong>
        <span class="badge ${escapeHtml(r.statusCls)}">${escapeHtml(r.status)}</span>
      </div>
      <p class="muted" style="margin:0;font-size:12px;line-height:1.4">${escapeHtml(r.blurb)}</p>
      ${r.href ? `<div class="croc-cc-tool-card__act"><a class="btn sm secondary btn-tap" href="${escapeHtml(r.href)}">Open</a></div>` : ""}
    </article>`,
    )
    .join("");

  return `<div class="croc-cc-tools" id="ccAllTools">
    <div class="croc-cc-tools__intro">
      <h4>All tools</h4>
      <p class="muted">Everything available for your account. Ready tools work now; others open a page you can explore.</p>
    </div>
    ${sections || `<p class="muted">No tools listed for this account yet.</p>`}
    <section class="croc-cc-tools__sec">
      <div class="croc-cc-tools__sec-head">
        <h5>Response network</h5>
        <span class="muted">preview</span>
      </div>
      <div class="croc-cc-tools__grid">${honesty}</div>
    </section>
  </div>`;
}
